import axios from 'utils/axios';
import type {
    ClientContactWriteType,
    GetVendorListResultType,
    ObjectCustomerType,
    VendorActionResultType,
    VendorCustomerType,
    VendorPersonContactItemType,
    VendorPersonType,
    VendorPersonWriteType,
    VendorType,
    VendorWriteType,
} from '@econtractor/types';

export interface VendorListParams {
    page?: number;
    onPage?: number;
    search?: string;
    order?: string;
}

const handleVendorApiError = (e: unknown): VendorActionResultType | null => {
    if (e && typeof e === 'object' && ('stat' in e || 'errCode' in e)) {
        return e as VendorActionResultType;
    }
    if (e && typeof e === 'object' && 'response' in e) {
        const response = (e as { response?: { data?: VendorActionResultType } }).response;
        if (response?.data) return response.data;
    }
    console.error(e);
    return null;
};

export const getList = async (getData: VendorListParams): Promise<GetVendorListResultType | null> => {
    try {
        const response = await axios('/vendors', { params: getData });
        return response?.data || null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const getVendorById = async (vendorId: number | string): Promise<VendorType | null> => {
    try {
        const response = await axios(`/vendors/${vendorId}`);
        return response?.data || null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const createVendor = async (payload: VendorWriteType): Promise<VendorActionResultType | null> => {
    try {
        const response = await axios.post('/vendors/new', payload);
        return response?.data || null;
    } catch (e) {
        return handleVendorApiError(e);
    }
};

export const updateVendor = async (
    vendorId: number | string,
    payload: VendorWriteType,
): Promise<VendorActionResultType | null> => {
    try {
        const response = await axios.post(`/vendors/${vendorId}`, payload);
        return response?.data || null;
    } catch (e) {
        return handleVendorApiError(e);
    }
};

export const deleteVendor = async (vendorId: number | string): Promise<VendorActionResultType | null> => {
    try {
        const response = await axios.delete(`/vendors/${vendorId}`);
        return response?.data || null;
    } catch (e) {
        return handleVendorApiError(e);
    }
};

export const getVendorCustomers = async (
    vendorId: number | string,
): Promise<VendorCustomerType[] | null> => {
    try {
        const response = await axios(`/vendors/${vendorId}/customers`);
        return Array.isArray(response?.data) ? response.data : null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const getVendorCustomer = async (
    vendorId: number | string,
    customerId: number | string,
): Promise<VendorCustomerType | null> => {
    try {
        const response = await axios(`/vendors/${vendorId}/customers/${customerId}`);
        return response?.data || null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export type SaveVendorCustomerResult =
    | { ok: true; id?: number }
    | { ok: false; errCode?: string };

export const upsertVendorCustomer = async (
    vendorId: number | string,
    customer: ObjectCustomerType,
): Promise<SaveVendorCustomerResult> => {
    try {
        const response = await axios.post(`/vendors/${vendorId}/customers`, { customer });
        if (response?.data?.stat) return { ok: true, id: response.data.id };
        return { ok: false, errCode: response?.data?.errCode };
    } catch (e: any) {
        const errCode = e?.errCode;
        if (errCode) return { ok: false, errCode };
        console.error(e);
        return { ok: false };
    }
};

export const updateVendorCustomer = async (
    vendorId: number | string,
    customerId: number | string,
    customer: ObjectCustomerType,
): Promise<SaveVendorCustomerResult> => {
    try {
        const response = await axios.post(`/vendors/${vendorId}/customers/${customerId}`, { customer });
        if (response?.data?.stat) return { ok: true };
        return { ok: false, errCode: response?.data?.errCode };
    } catch (e: any) {
        const errCode = e?.errCode;
        if (errCode) return { ok: false, errCode };
        console.error(e);
        return { ok: false };
    }
};

export const deleteVendorCustomer = async (
    vendorId: number | string,
    customerId: number | string,
): Promise<boolean> => {
    try {
        const response = await axios.delete(`/vendors/${vendorId}/customers/${customerId}`);
        return response?.data?.stat || false;
    } catch (e) {
        console.error(e);
        return false;
    }
};

export const getVendorPersons = async (
    vendorId: number | string,
): Promise<VendorPersonType[] | null> => {
    try {
        const response = await axios(`/vendors/${vendorId}/persons`);
        return Array.isArray(response?.data) ? response.data : null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export type SaveVendorPersonResult =
    | { ok: true; person: VendorPersonType }
    | { ok: false; errCode?: string };

export const createVendorPerson = async (
    vendorId: number | string,
    payload: VendorPersonWriteType,
): Promise<SaveVendorPersonResult> => {
    try {
        const response = await axios.post(`/vendors/${vendorId}/persons`, payload);
        if (response?.data?.stat && response?.data?.person) {
            return { ok: true, person: response.data.person };
        }
        return { ok: false, errCode: response?.data?.errCode };
    } catch (e: any) {
        const errCode = e?.errCode;
        if (errCode) return { ok: false, errCode };
        console.error(e);
        return { ok: false };
    }
};

export const updateVendorPerson = async (
    vendorId: number | string,
    personId: number | string,
    payload: VendorPersonWriteType,
): Promise<SaveVendorPersonResult> => {
    try {
        const response = await axios.post(`/vendors/${vendorId}/persons/${personId}`, payload);
        if (response?.data?.stat && response?.data?.person) {
            return { ok: true, person: response.data.person };
        }
        return { ok: false, errCode: response?.data?.errCode };
    } catch (e: any) {
        const errCode = e?.errCode;
        if (errCode) return { ok: false, errCode };
        console.error(e);
        return { ok: false };
    }
};

export const deleteVendorPerson = async (
    vendorId: number | string,
    personId: number | string,
): Promise<boolean> => {
    try {
        const response = await axios.delete(`/vendors/${vendorId}/persons/${personId}`);
        return response?.data?.stat || false;
    } catch (e) {
        console.error(e);
        return false;
    }
};

export const setVendorPersonPrimary = async (
    vendorId: number | string,
    personId: number | string,
    isPrimary: boolean,
): Promise<SaveVendorPersonResult> => {
    try {
        const response = await axios.post(`/vendors/${vendorId}/persons/${personId}/primary`, { isPrimary });
        if (response?.data?.stat && response?.data?.person) {
            return { ok: true, person: response.data.person };
        }
        return { ok: false, errCode: response?.data?.errCode };
    } catch (e: any) {
        const errCode = e?.errCode;
        if (errCode) return { ok: false, errCode };
        console.error(e);
        return { ok: false };
    }
};

export const getVendorPersonContacts = async (
    vendorId: number | string,
    personId: number | string,
): Promise<VendorPersonContactItemType[] | null> => {
    try {
        const response = await axios(`/vendors/${vendorId}/persons/${personId}/contacts`);
        return Array.isArray(response?.data) ? response.data : null;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export type SaveVendorPersonContactResult =
    | { ok: true; contact: VendorPersonContactItemType }
    | { ok: false; errCode?: string };

export const addVendorPersonContact = async (
    vendorId: number | string,
    personId: number | string,
    contact: ClientContactWriteType,
): Promise<SaveVendorPersonContactResult> => {
    try {
        const response = await axios.post(`/vendors/${vendorId}/persons/${personId}/contacts`, contact);
        if (response?.data?.stat && response?.data?.contact) {
            return { ok: true, contact: response.data.contact };
        }
        return { ok: false, errCode: response?.data?.errCode };
    } catch (e: any) {
        const errCode = e?.errCode;
        if (errCode) return { ok: false, errCode };
        console.error(e);
        return { ok: false };
    }
};

export const updateVendorPersonContact = async (
    vendorId: number | string,
    personId: number | string,
    contactId: number | string,
    contactValue: string,
): Promise<SaveVendorPersonContactResult> => {
    try {
        const response = await axios.post(`/vendors/${vendorId}/persons/${personId}/contacts/${contactId}`, {
            contactValue,
        });
        if (response?.data?.stat && response?.data?.contact) {
            return { ok: true, contact: response.data.contact };
        }
        return { ok: false, errCode: response?.data?.errCode };
    } catch (e: any) {
        const errCode = e?.errCode;
        if (errCode) return { ok: false, errCode };
        console.error(e);
        return { ok: false };
    }
};

export const deleteVendorPersonContact = async (
    vendorId: number | string,
    personId: number | string,
    contactId: number | string,
): Promise<boolean> => {
    try {
        const response = await axios.delete(`/vendors/${vendorId}/persons/${personId}/contacts/${contactId}`);
        return response?.data?.stat || false;
    } catch (e) {
        console.error(e);
        return false;
    }
};
